import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportSites } from "~pages/Sites/SiteListPage/hooks/useExportSites.ts";
import { useImportSites } from "~pages/Sites/SiteListPage/hooks/useImportSites.ts";
import type { CreateSite, UpdateSite } from "~pages/Sites/types/upsertSite.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateSite } from "./useCreateSite.ts";
import { useDeleteSite } from "./useDeleteSite.ts";
import { useUpdateSite } from "./useUpdateSite.ts";

export const useSiteService = () => {
  const { mutateAsync: createSiteFn, isPending: isCreatePending } = useCreateSite();
  const { mutateAsync: updateSiteFn, isPending: isUpdatePending } = useUpdateSite();
  const { mutateAsync: deleteSiteFn, isPending: isDeletePending } = useDeleteSite();
  const { mutateAsync: importSitesFn, isPending: isImportPending } = useImportSites();
  const { mutateAsync: exportSitesFn, isPending: isExportPending } = useExportSites();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.SITE);

  const createSite = async (payload: CreateSite) => {
    const createdSite = await create(() => createSiteFn(payload), {
      successMessageId: "site.add.success",
    });

    if (createdSite) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/sites`, { state: { search: payload.nameLocal } });
    }

    return createdSite;
  };

  const updateSite = (siteId: string, payload: Omit<UpdateSite, "siteId">) =>
    update(() => updateSiteFn({ ...payload, siteId }), {
      successMessageId: "site.update.success",
    });

  const deleteSite = async (siteId: string) =>
    Boolean(await remove(() => deleteSiteFn(siteId), { successMessageId: "site.delete.success" }));

  const importSites = async (fileName: string) =>
    executeOrThrow(() => importSitesFn(fileName), {
      successMessageId: "sites.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportSites = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportSitesFn(fieldNames), {
      successMessageId: "sites.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    createSite,
    isCreatePending,
    updateSite,
    isUpdatePending,
    deleteSite,
    isDeletePending,
    importSites,
    isImportPending,
    exportSites,
    isExportPending,
  };
};
